import { Box, Skeleton } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import { KitButton, KitCard, KitList, KitPill, SecH } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import { MobileActionItem, MobileListRow } from '../common/MobileCrud';
import { Kpi, KpiGrid, KitEmptyNote } from '../common/MobileDashboardKit';
import {
    KitAvatar,
    KitBanner,
    KitEmpty,
    KitEntityCard,
    KitFab,
    KitField,
    KitFormBar,
    KitInput,
    KitKV,
    KitSearch,
    KitSelect,
    KitStickyToolbar,
    KitSwitch,
    KitTextarea,
    KitToolbarButton,
} from '../common/MobileKitExtras';
import { useAppBarAction } from '../layout/AppBarActions';
import { APP_BAR_OFFSET } from '../layout/mobileShell';
import { formatBRMoney, parseBRMoney, PAYMENT_METHODS } from '../../pages/cashregisters/shared';

/**
 * Telas mobile de Caixas — `prototipos-mobile/caixas/*.html` (lista, form, detalhe, histórico,
 * abrir, fechar, sangria). Só apresentação: dados, permissões, chamadas e validações vêm de
 * `pages/cashregisters/*`.
 */

const SlidersIcon = protoIcon('sliders');
const PlusIcon = protoIcon('plus');
const UserIcon = protoIcon('user');
const WalletIcon = protoIcon('wallet');
const ReceiptIcon = protoIcon('receipt');
const UpRightIcon = protoIcon('upright');
const HistoryIcon = protoIcon('history');
const AlertIcon = protoIcon('alert');
const DothIcon = protoIcon('doth');
const EditIcon = protoIcon('edit');
const LinkIcon = protoIcon('link');
const TrashIcon = protoIcon('trash');

const PAGE_SIZE = 20;
const STRIPE_OPEN = '#16a34a';
const STRIPE_CLOSED = '#94a3b8';

const money = (value) => `R$ ${formatBRMoney(value)}`;

// "dd/mm/aaaa hh:mm:ss" (formato pronto do backend) -> partes
const datePart = (formatted) => String(formatted || '').split(' ')[0].slice(0, 5);
const timePart = (formatted) => (String(formatted || '').split(' ')[1] || '').slice(0, 5);

// "Caixa 01" -> "01"; sem número, as duas primeiras letras
function avatarText(name) {
    const digits = String(name || '').match(/\d+/);
    if (digits) return digits[0].slice(-2).padStart(2, '0');
    return String(name || '?').replace(/[^A-Za-zÀ-ÿ0-9]/g, '').slice(0, 2).toUpperCase() || '?';
}

const shadowMd = (theme) => (theme.palette.mode === 'dark' ? '0 6px 20px rgba(0,0,0,.35)' : '0 6px 20px rgba(15,23,42,.08)');

/* -------------------------------- Lista -------------------------------- */

function CashRegisterCard({ row, onOpen }) {
    const isOpen = row.status === 'open';
    return (
        <KitEntityCard stripeColor={isOpen ? STRIPE_OPEN : STRIPE_CLOSED} onClick={onOpen ? () => onOpen(row) : undefined}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <Box component="span" sx={{ fontWeight: 800, fontSize: 15, letterSpacing: '-.01em' }}>{row.name}</Box>
                <KitPill tone={isOpen ? 'success' : undefined}>{isOpen ? 'Aberto' : 'Fechado'}</KitPill>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', fontSize: 12, color: 'text.disabled', lineHeight: 1.4, mt: '8px' }}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><UserIcon size={13} />{row.operator || '—'}</Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px dashed', borderColor: 'divider', mt: '12px', pt: '10px' }}>
                <Box component="span" sx={{ fontWeight: 800, fontSize: 16, letterSpacing: '-.02em' }}>{money(parseBRMoney(row.availableChangeCash || row.available_cash))}</Box>
                <Box component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}>saldo</Box>
            </Box>
        </KitEntityCard>
    );
}

export function MobileCashRegisterList({
    rows,
    loading,
    search,
    onSearchChange,
    status,
    onStatusChange,
    onApplyFilters,
    onClearFilters,
    canCreate,
    onOpen,
    onNew,
}) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [limit, setLimit] = useState(PAGE_SIZE);
    const visible = rows.slice(0, limit);

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitStickyToolbar top={APP_BAR_OFFSET}>
                <KitSearch
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Buscar caixa…"
                    inputProps={{ 'aria-label': 'Buscar caixa', inputMode: 'search', enterKeyHint: 'search' }}
                />
                <KitToolbarButton
                    aria-label="Filtros"
                    onClick={() => setFiltersOpen(true)}
                    sx={status ? { color: 'primary.main', borderColor: 'primary.main' } : undefined}
                >
                    <SlidersIcon size={20} />
                </KitToolbarButton>
            </KitStickyToolbar>

            <Box sx={{ mt: '12px' }} />

            {loading && rows.length === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={118} sx={{ borderRadius: '16px' }} />)}
                </Box>
            )}

            {!loading && rows.length === 0 && (
                <KitEmpty icon={WalletIcon} title="Nenhum caixa encontrado">
                    Ajuste a busca ou o filtro para ver outros caixas.
                </KitEmpty>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {visible.map((row) => <CashRegisterCard key={row.id} row={row} onOpen={onOpen} />)}
            </Box>

            {rows.length > limit && (
                <KitButton block sx={{ mt: '14px' }} onClick={() => setLimit((l) => l + PAGE_SIZE)}>Carregar mais</KitButton>
            )}

            {canCreate && <KitFab label="Novo" icon={PlusIcon} onClick={onNew} />}

            <MobileSheet
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                title="Filtros"
                footer={(
                    <>
                        <KitButton sx={{ flex: 1 }} onClick={() => { setFiltersOpen(false); onClearFilters(); }}>Limpar</KitButton>
                        <KitButton variant="primary" sx={{ flex: 1 }} onClick={() => { setFiltersOpen(false); onApplyFilters(); }}>Aplicar</KitButton>
                    </>
                )}
            >
                <KitField label="Status">
                    <KitSelect value={status} onChange={(e) => onStatusChange(e.target.value)} aria-label="Status">
                        <option value="">Todos</option>
                        <option value="open">Aberto</option>
                        <option value="closed">Fechado</option>
                    </KitSelect>
                </KitField>
            </MobileSheet>
        </Box>
    );
}

/* ------------------------------- Formulário ------------------------------ */

export function MobileCashRegisterForm({
    name,
    onNameChange,
    nameError,
    active,
    onActiveChange,
    allowsBleeding,
    onAllowsBleedingChange,
    loading,
    saving,
    onCancel,
    onSave,
}) {
    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }} component="form" onSubmit={(e) => { e.preventDefault(); onSave(); }} noValidate>
            <KitField label="Nome" required help={nameError || undefined} error={Boolean(nameError)}>
                <KitInput
                    type="text"
                    value={name}
                    onChange={(e) => onNameChange(e.target.value)}
                    placeholder="Ex.: Caixa 03"
                    maxLength={60}
                    error={Boolean(nameError)}
                    disabled={loading}
                />
            </KitField>

            <KitSwitch title="Ativo" description="Caixas inativos ficam ocultos na abertura de caixa do PDV" checked={active} onChange={(e) => onActiveChange(e.target.checked)} disabled={loading} />
            <KitSwitch title="Permitir sangria" description="Libera o registro de sangrias enquanto este caixa estiver aberto" checked={allowsBleeding} onChange={(e) => onAllowsBleedingChange(e.target.checked)} disabled={loading} />

            <KitFormBar>
                <KitButton onClick={onCancel}>Cancelar</KitButton>
                <KitButton variant="primary" onClick={onSave} disabled={saving || loading}>
                    {saving ? 'Salvando...' : 'Salvar'}
                </KitButton>
            </KitFormBar>
        </Box>
    );
}

/* -------------------------------- Detalhe -------------------------------- */

export function MobileCashRegisterDetail({
    loading,
    cash,
    isOpen,
    operator,
    openDate,
    balance,
    initialAmount,
    summary,
    justClosed,
    canOpen,
    canClose,
    canBleed,
    canHistory,
    canUpdate,
    canDelete,
    onOpenCash,
    onCloseCash,
    onSangria,
    onHistory,
    onEdit,
    onIntegration,
    onDelete,
}) {
    const [moreOpen, setMoreOpen] = useState(false);
    const moreItems = [
        ...(canUpdate ? [{ label: 'Editar', icon: EditIcon, onClick: onEdit }] : []),
        { label: 'Integração', icon: LinkIcon, onClick: onIntegration },
        ...(canDelete && !isOpen ? [{ label: 'Excluir', icon: TrashIcon, onClick: onDelete, destructive: true }] : []),
    ];

    useAppBarAction({
        enabled: Boolean(cash),
        label: 'Mais',
        icon: DothIcon,
        onClick: () => setMoreOpen(true),
    });

    if (loading || !cash) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Skeleton variant="rounded" height={118} sx={{ borderRadius: '16px' }} />
                <Skeleton variant="rounded" height={90} sx={{ borderRadius: '16px' }} />
            </Box>
        );
    }

    const entradas = summary.vendasDinheiro + summary.pix + summary.machineGross;
    const payments = PAYMENT_METHODS
        .map((m) => ({ ...m, value: m.key === 'dinheiro' ? summary.vendasDinheiro : m.key === 'pix' ? summary.pix : summary.machineGross }))
        .filter((m) => m.value > 0);
    const hasMovements = payments.length > 0 || summary.sangriasList.length > 0;

    const who = operator ? ` por ${operator}` : '';
    const subLine = isOpen
        ? [`Aberto${who}`, timePart(openDate) || null, `fundo ${money(initialAmount)}`].filter(Boolean).join(' · ')
        : [`Fechado${who}`, openDate ? `desde ${openDate}` : null].filter(Boolean).join(' · ');
    const showBar = isOpen ? canClose : canOpen;

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <Box
                sx={(theme) => ({
                    backgroundImage: theme.palette.gradient.brand,
                    color: '#fff',
                    borderRadius: '16px',
                    p: '16px',
                    boxShadow: shadowMd(theme),
                })}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <Box component="span" sx={{ fontSize: 12.5, opacity: 0.85 }}>Saldo atual</Box>
                    <KitPill sx={{ '&::before': { display: 'none' } }}>{isOpen ? 'Aberto' : 'Fechado'}</KitPill>
                </Box>
                <Box sx={{ fontWeight: 800, fontSize: 32, letterSpacing: '-.03em' }}>{money(balance)}</Box>
                <Box sx={{ fontSize: 12.5, opacity: 0.85 }}>{subLine}</Box>
            </Box>

            {justClosed && (
                <Box sx={{ mt: '12px' }}>
                    <KitBanner
                        tone={Math.abs(justClosed.diff) < 0.005 ? 'success' : undefined}
                        icon={AlertIcon}
                        iconSize={18}
                        title={Math.abs(justClosed.diff) < 0.005
                            ? 'Caixa conferido'
                            : `Diferença de ${money(Math.abs(justClosed.diff))} ${justClosed.diff < 0 ? 'a menos' : 'a mais'}`}
                    >
                        Valor apurado no fechamento: {money(justClosed.totalCounted)}
                    </KitBanner>
                </Box>
            )}

            <SecH title="Movimentação do turno" />
            <KpiGrid first>
                <Kpi label="Entradas" icon="downleft" value={`R$ ${Math.floor(entradas).toLocaleString('pt-BR')}`} compact />
                <Kpi label="Saídas" icon="upright" value={`R$ ${Math.floor(summary.totalSangrias).toLocaleString('pt-BR')}`} delta={{ text: 'sangria', tone: 'down' }} />
            </KpiGrid>

            <Box sx={{ mt: '12px' }}>
                {hasMovements ? (
                    <KitList>
                        {payments.map((m) => (
                            <MobileListRow
                                key={m.key}
                                icon={ReceiptIcon}
                                title={m.label === 'Cartão (débito + crédito)' ? 'Cartão' : m.label}
                                sub="vendas do turno"
                                end={<Box sx={{ fontWeight: 800, fontSize: 14, color: 'success.main' }}>+ {money(m.value)}</Box>}
                            />
                        ))}
                        {summary.sangriasList.map((s, i) => (
                            <MobileListRow
                                key={`${s.DateOperation}-${i}`}
                                icon={UpRightIcon}
                                tone="red"
                                title="Sangria"
                                sub={[timePart(s.DateOperationFormatted), s.ReasonBleending].filter(Boolean).join(' · ')}
                                end={<Box sx={{ fontWeight: 800, fontSize: 14, color: 'error.main' }}>- {money(parseBRMoney(s.Amount))}</Box>}
                            />
                        ))}
                    </KitList>
                ) : (
                    <KitCard><KitEmptyNote>Nenhuma movimentação neste turno.</KitEmptyNote></KitCard>
                )}
            </Box>

            {(canBleed || canHistory) && (
                <Box sx={{ display: 'grid', gridTemplateColumns: canBleed && canHistory ? '1fr 1fr' : '1fr', gap: '12px', mt: '16px' }}>
                    {canBleed && (
                        <KitButton onClick={onSangria} disabled={!isOpen}><UpRightIcon size={18} /> Sangria</KitButton>
                    )}
                    {canHistory && (
                        <KitButton onClick={onHistory}><HistoryIcon size={18} /> Histórico</KitButton>
                    )}
                </Box>
            )}

            {showBar && (
                <KitFormBar>
                    {isOpen
                        ? <KitButton variant="danger" onClick={onCloseCash}>Fechar caixa</KitButton>
                        : <KitButton variant="primary" onClick={onOpenCash}>Abrir caixa</KitButton>}
                </KitFormBar>
            )}

            <MobileSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Mais">
                {moreItems.map((item) => (
                    <MobileActionItem
                        key={item.label}
                        icon={item.icon}
                        label={item.label}
                        destructive={item.destructive}
                        chevron={!item.destructive}
                        onClick={() => { setMoreOpen(false); item.onClick(); }}
                    />
                ))}
            </MobileSheet>
        </Box>
    );
}

/* ------------------------------- Histórico ------------------------------- */

const EVENT_ROW = {
    open: { icon: WalletIcon, tone: 'green' },
    cash_bleeding: { icon: UpRightIcon, tone: 'red' },
    closed: { icon: WalletIcon, tone: 'gray' },
};

export function MobileCashHistory({
    loading,
    events,
    eventLabels,
    search,
    onSearchChange,
    type,
    onTypeChange,
    sort,
    onSortChange,
    onApplyFilters,
    onClearFilters,
    filtersActive,
}) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [limit, setLimit] = useState(PAGE_SIZE);
    const visible = events.slice(0, limit);

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitStickyToolbar top={APP_BAR_OFFSET}>
                <KitSearch
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Buscar por data…"
                    inputProps={{ 'aria-label': 'Buscar no histórico', inputMode: 'search', enterKeyHint: 'search' }}
                />
                <KitToolbarButton
                    aria-label="Filtros"
                    onClick={() => setFiltersOpen(true)}
                    sx={filtersActive ? { color: 'primary.main', borderColor: 'primary.main' } : undefined}
                >
                    <SlidersIcon size={20} />
                </KitToolbarButton>
            </KitStickyToolbar>

            <Box sx={{ mt: '12px' }} />

            {loading && events.length === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={60} sx={{ borderRadius: '16px' }} />)}
                </Box>
            )}

            {!loading && events.length === 0 && (
                <KitEmpty icon={HistoryIcon} title="Nenhum evento encontrado">
                    Ajuste a busca ou os filtros para ver outros registros deste caixa.
                </KitEmpty>
            )}

            {visible.length > 0 && (
                <KitList>
                    {visible.map((ev, i) => {
                        const meta = EVENT_ROW[ev.TypeOperation] || EVENT_ROW.closed;
                        const isBleed = ev.TypeOperation === 'cash_bleeding';
                        return (
                            <MobileListRow
                                key={`${ev.DateOperation}-${i}`}
                                icon={meta.icon}
                                tone={meta.tone}
                                title={`${datePart(ev.DateOperationFormatted)} · ${eventLabels[ev.TypeOperation] || ''}`}
                                sub={[ev.User, timePart(ev.DateOperationFormatted), isBleed ? ev.ReasonBleending : null].filter(Boolean).join(' · ')}
                                end={<Box sx={{ fontWeight: 800, fontSize: 14, color: isBleed ? 'error.main' : undefined }}>{isBleed ? '- ' : ''}R$ {ev.Amount}</Box>}
                            />
                        );
                    })}
                </KitList>
            )}

            {events.length > limit && (
                <KitButton block sx={{ mt: '14px' }} onClick={() => setLimit((l) => l + PAGE_SIZE)}>Carregar mais</KitButton>
            )}

            <MobileSheet
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                title="Filtros"
                footer={(
                    <>
                        <KitButton sx={{ flex: 1 }} onClick={() => { setFiltersOpen(false); onClearFilters(); }}>Limpar</KitButton>
                        <KitButton variant="primary" sx={{ flex: 1 }} onClick={() => { setFiltersOpen(false); onApplyFilters(); }}>Aplicar</KitButton>
                    </>
                )}
            >
                <KitField label="Tipo de evento">
                    <KitSelect value={type} onChange={(e) => onTypeChange(e.target.value)} aria-label="Tipo de evento">
                        <option value="">Todos</option>
                        <option value="open">Abertura</option>
                        <option value="closed">Fechamento</option>
                        <option value="cash_bleeding">Sangria</option>
                    </KitSelect>
                </KitField>
                <KitField label="Ordenar por">
                    <KitSelect value={sort} onChange={(e) => onSortChange(e.target.value)} aria-label="Ordenar por">
                        <option value="desc">Mais recentes primeiro</option>
                        <option value="asc">Mais antigos primeiro</option>
                    </KitSelect>
                </KitField>
            </MobileSheet>
        </Box>
    );
}

/* ---------------------------- Abrir / Sangria ---------------------------- */

const moneyInputProps = { type: 'text', inputMode: 'decimal', autoComplete: 'off' };

export function MobileOpenCash({ name, operator, amount, onAmountChange, amountError, obs, onObsChange, saving, onCancel, onConfirm }) {
    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }} component="form" onSubmit={(e) => { e.preventDefault(); onConfirm(); }} noValidate>
            <KitCard sx={{ textAlign: 'center' }}>
                <KitAvatar text={avatarText(name)} size={76} sx={{ m: '0 auto 8px' }} />
                <Box sx={{ fontWeight: 700 }}>{name}</Box>
                <Box sx={{ fontSize: 12.5, color: 'text.disabled' }}>Operador: {operator || '—'}</Box>
            </KitCard>

            <Box sx={{ mt: '16px' }} />
            <KitField label="Fundo de troco (R$)" required help={amountError || undefined} error={Boolean(amountError)}>
                <KitInput {...moneyInputProps} value={amount} onChange={(e) => onAmountChange(e.target.value)} error={Boolean(amountError)} />
            </KitField>
            <KitField label="Observação">
                <KitTextarea value={obs} onChange={(e) => onObsChange(e.target.value)} />
            </KitField>

            <KitFormBar>
                <KitButton onClick={onCancel} disabled={saving}>Cancelar</KitButton>
                <KitButton variant="primary" onClick={onConfirm} disabled={saving}>{saving ? 'Salvando...' : 'Abrir caixa'}</KitButton>
            </KitFormBar>
        </Box>
    );
}

export const SANGRIA_REASONS = ['Depósito bancário', 'Pagamento a fornecedor', 'Outro'];

export function MobileSangria({
    balance,
    amount,
    onAmountChange,
    amountError,
    reasonOption,
    onReasonOptionChange,
    reasonError,
    obs,
    onObsChange,
    obsMaxLength,
    saving,
    onCancel,
    onConfirm,
}) {
    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }} component="form" onSubmit={(e) => { e.preventDefault(); onConfirm(); }} noValidate>
            <KitCard sx={{ boxShadow: 'none' }}>
                <KitKV label="Saldo em dinheiro" value={money(balance)} />
            </KitCard>

            <Box sx={{ mt: '16px' }} />
            <KitField label="Valor da retirada (R$)" required help={amountError || undefined} error={Boolean(amountError)}>
                <KitInput {...moneyInputProps} value={amount} onChange={(e) => onAmountChange(e.target.value)} error={Boolean(amountError)} />
            </KitField>
            <KitField label="Motivo" help={reasonError || undefined} error={Boolean(reasonError)}>
                <KitSelect value={reasonOption} onChange={(e) => onReasonOptionChange(e.target.value)}>
                    {SANGRIA_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </KitSelect>
            </KitField>
            <KitField label="Observação">
                <KitTextarea value={obs} onChange={(e) => onObsChange(e.target.value)} maxLength={obsMaxLength} />
            </KitField>

            <KitFormBar>
                <KitButton onClick={onCancel} disabled={saving}>Cancelar</KitButton>
                <KitButton variant="primary" onClick={onConfirm} disabled={saving}>{saving ? 'Salvando...' : 'Registrar sangria'}</KitButton>
            </KitFormBar>
        </Box>
    );
}

/* --------------------------------- Fechar -------------------------------- */

const isoValue = (d) => (d && d.isValid() ? d.format('YYYY-MM-DD') : '');

export function MobileCloseCash({ close, summary, sessionLabel, onCancel }) {
    const {
        counted, changeCounted, observation, setObservation, saving, startDate, setStartDate, endDate, setEndDate,
        loadingPeriod, periodError, rangeError, effectiveExpected, totals, confirm,
    } = close;
    const diff = totals.diff;
    const balanced = Math.abs(diff) < 0.005;
    const countedLabels = { dinheiro: 'Valor contado (R$)', pix: 'Contado em PIX (R$)', cartao: 'Contado em cartão (R$)' };

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }} component="form" onSubmit={(e) => { e.preventDefault(); confirm(); }} noValidate>
            <KitCard>
                <KitKV totals label="Fundo de troco" value={money(summary.valorInicial)} />
                <KitKV totals label="Vendas em dinheiro" value={money(summary.vendasDinheiro)} />
                <KitKV totals label="Sangrias" value={`- ${money(summary.totalSangrias)}`} />
                <KitKV totals big label="Esperado em caixa" value={money(summary.valorEsperado)} />
            </KitCard>

            <Box sx={{ mt: '16px' }} />
            {PAYMENT_METHODS.map((m, index) => {
                // PIX/cartão só aparecem quando há valor esperado/contado (o protótipo só conta o dinheiro).
                const hasValue = (effectiveExpected[m.key] || 0) > 0 || parseBRMoney(counted[m.key]) > 0;
                if (index > 0 && !hasValue) return null;
                return (
                    <KitField
                        key={m.key}
                        label={countedLabels[m.key]}
                        required={index === 0}
                        help={index === 0 ? undefined : `Esperado: ${money(effectiveExpected[m.key] || 0)}`}
                    >
                        <KitInput {...moneyInputProps} value={counted[m.key] || ''} onChange={(e) => changeCounted(m.key, e.target.value)} />
                    </KitField>
                );
            })}

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <KitField label="Início" sx={{ mb: 0 }}>
                    <KitInput type="date" value={isoValue(startDate)} max={isoValue(endDate) || undefined} onChange={(e) => setStartDate(e.target.value ? dayjs(e.target.value) : null)} disabled={saving} />
                </KitField>
                <KitField label="Fim" sx={{ mb: 0 }}>
                    <KitInput type="date" value={isoValue(endDate)} min={isoValue(startDate) || undefined} max={dayjs().format('YYYY-MM-DD')} onChange={(e) => setEndDate(e.target.value ? dayjs(e.target.value) : null)} disabled={saving} />
                </KitField>
            </Box>
            <Box sx={{ fontSize: 12, color: rangeError || periodError ? 'error.main' : 'text.disabled', m: '5px 2px 14px', lineHeight: 1.4 }}>
                {rangeError || periodError || `Sessão aberta em ${sessionLabel || '—'}. Os valores esperados consideram as vendas do período escolhido.`}
            </Box>

            {balanced ? (
                <KitBanner tone="success" icon={AlertIcon} iconSize={18} title="Valores conferidos">Sem diferença de caixa.</KitBanner>
            ) : (
                <KitBanner icon={AlertIcon} iconSize={18} title={`Diferença de ${money(Math.abs(diff))} ${diff < 0 ? 'a menos' : 'a mais'}`}>
                    Informe uma observação.
                </KitBanner>
            )}

            <Box sx={{ mt: '12px' }} />
            <KitField label="Observação">
                <KitTextarea value={observation} onChange={(e) => setObservation(e.target.value)} placeholder="Ex.: troco devolvido ao cliente" />
            </KitField>

            <KitFormBar>
                <KitButton onClick={onCancel} disabled={saving}>Cancelar</KitButton>
                <KitButton variant="primary" onClick={confirm} disabled={saving || loadingPeriod || Boolean(rangeError) || Boolean(periodError)}>
                    {saving ? 'Fechando...' : 'Fechar caixa'}
                </KitButton>
            </KitFormBar>
        </Box>
    );
}
